import { isCustomPatternLayer, type Layer } from '@pn/core/domain/layer';
import {
  generatePatternLayerId,
  getPointsLayerId,
  getSourceId,
} from '@pn/core/domain/workspace';
import { layerIdsToHighlight } from '@pn/core/operations/workspace/hooks/useLayerHighlighting';
import type { MapboxIMap } from '@pn/services/map/mapbox';
import { customLayersRegistry } from '@pn/services/map/mapbox/custom/registry';
import { throwOnMapboxErrors } from '@pn/services/map/mapbox/mapboxUtils';
import { generateEmptyGeoJsonFeatureCollection } from '@pn/services/utils/geojson';
import { isNil } from 'lodash-es';
import { buildHighlightLayer } from './layerBuilders/buildHighlightLayer';
import { buildPatternLayer } from './layerBuilders/buildPatternLayer';
import { buildPointsLayers } from './layerBuilders/buildPointsLayers';
import { buildPrimaryLayers } from './layerBuilders/buildPrimaryLayers';

export const addDataLayer: MapboxIMap['addDataLayer'] = function (
  this: MapboxIMap,
  layer,
  { beforeLayerId, sourceLayer, style, selectionStyle }
) {
  if (this.hasLayer(layer.id)) {
    throw new Error(`Layer ${layer.id} exists already`);
  }

  const sourceIds = [
    getSourceId(layer.id),
    getSourceId(getPointsLayerId(layer.id)),
  ];
  const existingSourceIds = sourceIds.filter(
    (id) => !isNil(this._native.getSource(id))
  );
  const mapboxLayerIds: string[] = [];

  try {
    throwOnMapboxErrors(this._native, `Failed to add layer ${layer.id}`, () => {
      const { sourceId, pointsSourceId } = prepareSources(this._native, {
        layer,
        sourceLayer,
      });

      const mapboxLayers: mapboxgl.AnyLayer[] = [];

      const primaryLayers = buildPrimaryLayers({
        layer,
        sourceId,
        style,
        selectionStyle,
      });

      if (isCustomPatternLayer(layer)) {
        const patternLayer = buildPatternLayer(layer);
        primaryLayers.splice(1, 0, patternLayer); // insert between primary and selection layers
        customLayersRegistry.set(patternLayer.id, patternLayer);
      }

      mapboxLayers.push(...primaryLayers);

      if (!isNil(pointsSourceId)) {
        mapboxLayers.push(
          ...buildPointsLayers({
            layer,
            pointsSourceId,
            selectionStyle,
          })
        );
      }

      if (layerIdsToHighlight.includes(layer.id)) {
        mapboxLayers.push(buildHighlightLayer({ layer, sourceId, style }));
      }

      for (const mapboxLayer of mapboxLayers) {
        mapboxLayerIds.push(mapboxLayer.id);
        this._native.addLayer(mapboxLayer, beforeLayerId);
      }
    });
  } catch (error) {
    /**
     * Mapbox may have accepted some of the layers (e.g. the selection layer)
     * and rejected others. Remove whatever got added so that a failed layer
     * leaves no leftovers that would make the next attempt fail with
     * "already exists".
     */
    mapboxLayerIds
      .filter((id) => !isNil(this._native.getLayer(id)))
      .forEach((id) => this._native.removeLayer(id));

    customLayersRegistry.delete(generatePatternLayerId(layer.id));

    sourceIds
      .filter((id) => !existingSourceIds.includes(id))
      .filter((id) => !isNil(this._native.getSource(id)))
      .forEach((id) => this._native.removeSource(id));

    throw error;
  }
};

function prepareSources(
  map: mapboxgl.Map,
  params: {
    layer: Layer;
    sourceLayer?: Layer;
  }
) {
  const { layer, sourceLayer } = params;

  const sourceId = getSourceId(layer.id);

  if (isNil(map.getSource(sourceId))) {
    map.addSource(sourceId, layer.source);
  }

  /**
   * Add source item's vector tileset to allow for feature state updates.
   * This is necessary when visualizing a list before its source item was
   * visualized.
   */
  if (!isNil(sourceLayer)) {
    const sourceLayerSourceId = getSourceId(sourceLayer.id);
    if (
      sourceLayer.source.type === 'vector' &&
      isNil(map.getSource(sourceLayerSourceId))
    ) {
      map.addSource(sourceLayerSourceId, sourceLayer.source);
    }
  }

  if (layer.renderAsPoints) {
    const pointsSourceId = getSourceId(getPointsLayerId(layer.id));
    if (isNil(map.getSource(pointsSourceId))) {
      map.addSource(pointsSourceId, {
        type: 'geojson',
        data: generateEmptyGeoJsonFeatureCollection(),
      });
    }

    return { sourceId, pointsSourceId };
  } else {
    return { sourceId };
  }
}
